/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import { updateError } from './user';
import { dispatch, store } from '../store';
import { getPracticeId } from './clincheck';
import { updateCompleteStatus } from './lead';
import { callApi } from '../../utils/apiCall';
import { getPaymentType, handleEditPaymentSuccess } from '../../utils/depositUtils';
// Lazy/circular-safe: appointmentThunks.js imports creteTreatmentPaymentFlow
// from this module, and this module needs followUpSlice back to refresh the
// lead after a deposit payment saves. Both are hoisted function declarations
// only ever invoked from inside a thunk body (never at module-eval time), so
// the import cycle resolves fine under ESM live bindings.
import { followUpSlice } from './lead/appointmentThunks';

const initialState = {
    depositData: null,
    depositPaymentDataGlobal: [],
    depositLoading: false,
};

const slice = createSlice({
    name: 'deposit',
    initialState,
    reducers: {
        setDepositData(state, action) {
            state.depositData = action.payload;
        },
        setDepositPaymentDataGlobal(state, action) {
            state.depositPaymentDataGlobal = action.payload;
        },
        setDepositLoading(state, action) {
            state.depositLoading = action.payload;
        },
    }
});

export default slice.reducer;

export const { setDepositData, setDepositPaymentDataGlobal, setDepositLoading } = slice.actions;

const handleSuccess = ({ leadTreatmentId, leadId }) => {
    const { practice } = store.getState().practice;
    const { depositData } = store.getState().deposit;
    const { depositAmount, paymentDate, additionalAmount, paymentMethod, appointment } = depositData;
    const paymentProcessorId = practice?.payment_processors?.find((ele) => paymentMethod?.includes(ele?.payment_processor))?.id || practice?.payment_processors?.[0]?.id;
    // See appointmentField comment in creteTreatmentPaymentFlow above — same
    // contract, this follow-on payment is for the same appointment.
    const appointmentField = appointment != null ? { appointment } : {};
    // Same lead refetch creteTreatmentPaymentFlow fires after the primary
    // deposit payment — also needed here since this second, combo-type
    // payment (DEPOSIT_AND_*) lands after that first refetch already ran.
    const refetchLead = () => dispatch(followUpSlice(leadId, getPracticeId(), 'LeadDetail'));
    switch (paymentMethod) {
        case 'DEPOSIT_AND_GOCARDLESS':
        case 'DEPOSIT_AND_STRIPE':
            dispatch(createTreatmentPayment({
                body: {
                    payment_processor: paymentProcessorId || 0,
                    amount: additionalAmount,
                    paid_at: paymentDate,
                    ...appointmentField,
                }, treatmentId: leadTreatmentId, leadId,
                handleSuccess: refetchLead,
            }));
            return;
        case 'DEPOSIT_AND_BALANCE':
            dispatch(createTreatmentPayment({
                body: {
                    payment_type: 'TREATMENT',
                    amount: additionalAmount,
                    paid_at: paymentDate,
                    ...appointmentField,
                }, treatmentId: leadTreatmentId, leadId,
                handleSuccess: refetchLead,
            }));
            break;
        case 'DEPOSIT_AND_FINANCE':
            dispatch(createTreatmentPayment({
                body: {
                    payment_type: 'FINANCE',
                    amount: depositAmount,
                    paid_at: paymentDate,
                    ...appointmentField,
                }, treatmentId: leadTreatmentId, leadId,
                handleSuccess: refetchLead,
            }));
            break;
        default:
            break;
    }
};

export function creteTreatmentPaymentFlow() {
    return async () => {
        try {
            const { followLead, createLead } = store.getState().lead;
            const { depositData } = store.getState().deposit;
            const { depositAmount, paymentDate, paymentMethod, treatmentId, depositPaymentData, appointment } = depositData;
            const leadId = followLead?.id || createLead?.id;
            const leadTreatmentId = treatmentId || createLead?.lead_treatment?.id;
            dispatch(setDepositData({ ...depositData, isDepositFlow: false }));
            // Contract: Payment create/update accepts an optional `appointment` id —
            // set on addAppointment (appointmentThunks.js) right after the new
            // appointment is created, so the deposit payment it triggers gets
            // linked to it. Omitted entirely (not sent as null) when absent, so a
            // backend that doesn't understand the field yet sees no change.
            const appointmentField = appointment != null ? { appointment } : {};
            // Refetch the lead (via followUpSlice, same call TreatmentAccordion's
            // handleUpdate uses) so the appointments list — and the
            // AppointmentDepositBadge it feeds — reflects the deposit just saved
            // without a manual page reload. Fired alongside each payment's own
            // handleSuccess, not instead of it.
            const refetchLead = () => dispatch(followUpSlice(leadId, getPracticeId(), 'LeadDetail'));
            if (depositPaymentData?.length > 0) {
                dispatch(editTreatmentPayment({
                    body: {
                        payment_type: getPaymentType(paymentMethod),
                        amount: depositAmount,
                        paid_at: paymentDate,
                        ...appointmentField,
                    }, treatmentId, leadId, paymentId: depositPaymentData?.[0]?.id,
                    handleSuccess: () => {
                        handleEditPaymentSuccess({ data: depositData, depositPaymentData, treatmentId, leadId });
                        refetchLead();
                    }
                }));
                return;
            }
            dispatch(createTreatmentPayment({
                body: {
                    payment_type: getPaymentType(paymentMethod),
                    amount: depositAmount,
                    paid_at: paymentDate,
                    ...appointmentField,
                }, treatmentId: leadTreatmentId, leadId,
                handleSuccess: () => {
                    handleSuccess({ leadId, leadTreatmentId });
                    refetchLead();
                }
            }));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}

export function getTreatmentPayment(data) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data.leadId}/treatments/${data.treatmentId}/payments/`);
            dispatch(setDepositLoading(false));
            if (response.status === 200) {
                dispatch(setDepositPaymentDataGlobal(response?.data?.results));
            } else {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(setDepositLoading(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}
export function createTreatmentPayment(data) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data.leadId}/treatments/${data.treatmentId}/payments/`, 'post', data.body);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Treatment payment done successfully' }));
                if (data.handleSuccess) {
                    // Hand the created payment back so callers can reference it — the
                    // Treatment Started payment cell puts its id/type/amount into the
                    // checklist item's response_payload when it completes the step.
                    // Existing callers take no argument and are unaffected.
                    data.handleSuccess(response.data);
                }
            } else {
                dispatch(setDepositLoading(false));
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                if (data.handleError) data.handleError();
            }
        } catch (error) {
            dispatch(setDepositLoading(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
            if (data.handleError) data.handleError();
        }
    };
}

export function editTreatmentPayment(data) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data.leadId}/treatments/${data.treatmentId}/payments/${data.paymentId}`, 'patch', data.body);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Treatment payment updated successfully' }));
                if (data.handleSuccess) {
                    data.handleSuccess();
                }
            } else {
                dispatch(setDepositLoading(false));
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(setDepositLoading(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}

export function deleteTreatmentPayment(data) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data.leadId}/treatments/${data.treatmentId}/payments/${data.paymentId}`, 'delete');
            if (response.status === 204) {
                dispatch(updateError({ success: true, message: 'Treatment payment deleted successfully' }));
                if (data.handleSuccess) {
                    data.handleSuccess();
                }
            } else {
                dispatch(setDepositLoading(false));
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(setDepositLoading(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}
